import { Fragment } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Box } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useFormikContext } from "formik";

type DateSelectProps<DateName extends string> = {
  dateName: DateName;
  minDate: Date;
  maxDate?: Date;
};

type DateFieldId<T extends string> = `${T}-${"start" | "end"}-date`;

type DateFormValues<T extends string> = {
  [K in DateFieldId<T>]: number;
};

function DateSelect<DateName extends string = string>({
  dateName,
  minDate,
  maxDate,
}: DateSelectProps<DateName>) {
  const { handleChange, values, errors } =
    useFormikContext<DateFormValues<DateName>>();

  const dates = (["start", "end"] as const).map((type) => ({
    type,
    label: type === "start" ? "From" : "To",
    id: `${dateName}-${type}-date` as DateFieldId<DateName>,
  }));

  const getDateValue = (timestamp: number) => {
    return timestamp ? new Date(timestamp) : null;
  };

  return (
    <Box
      data-testid="date-select"
      sx={{
        display: "flex",
        direction: "row",
        minWidth: "292px",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {dates.map((date) => (
          <Fragment key={date.id}>
            <DesktopDatePicker<Date>
              label={date.label}
              onChange={(value: Date | null) => {
                if (!value) {
                  return;
                }
                const dateValue = new Date(value);

                if (date.type === "start") {
                  dateValue.setHours(0, 0, 0, 0);
                }
                if (date.type === "end") {
                  dateValue.setHours(23, 59, 59, 999);
                }

                handleChange({
                  target: { id: date.id, value: dateValue.getTime() },
                });
              }}
              value={getDateValue(values[date.id])}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  id: date.id,
                  name: date.id,
                  error: Boolean(errors?.[date.id]),
                  helperText: errors?.[date.id] as React.ReactNode,
                  sx: { paddingRight: 2 },
                  variant: "standard",
                  inputProps: { "data-testid": date.id },
                },
              }}
            />
          </Fragment>
        ))}
      </LocalizationProvider>
    </Box>
  );
}

export default DateSelect;
